import type {SearchFilters} from "@/entities/rental";

import {buildQueryString, DateHelper, parseFloat, parseNumber, parseString} from "@/shared/lib";

/**
 * Default search filters (US overview map)
 * Only map bounds + zoom — these define the "default search" view
 */
const DEFAULT_FILTERS: Partial<SearchFilters> = {
    neLat: 51.13733623519548,
    neLng: -73.1860374737491,
    swLat: 6.947186177284353,
    swLng: -123.6352562237491,
    centerLat: 31.59634949038625,
    centerLng: -98.4106468487491,
    zoom: 4,
};

/**
 * Search route definition
 * Parses URL params directly to SearchFilters (camelCase format for Vacasa API)
 */
export const searchRoute = {
    path: "/search",

    defaults: DEFAULT_FILTERS,

    resolve: (filters: SearchFilters): SearchFilters => {
        const hasLocation = Boolean(filters.location?.trim());
        const hasMapBounds = [filters.neLat, filters.neLng, filters.swLat, filters.swLng].every((value) => typeof value === "number");

        return hasLocation || hasMapBounds ? filters : {...filters, ...DEFAULT_FILTERS};
    },

    /**
     * Generate search URL from SearchFilters
     */
    url: (filters?: SearchFilters) => {
        if (!filters) return "/search";

        // Convert SearchFilters to URL query params
        const query: Record<string, string | number> = {};

        if (filters.location) query.location = filters.location;

        // Convert startDate/endDate to checkIn/checkOut for URL
        if (filters.startDate) query.checkIn = DateHelper.formatDateForUrl(filters.startDate);
        if (filters.endDate) query.checkOut = DateHelper.formatDateForUrl(filters.endDate);

        // Flexible dates
        if (filters.flexible !== undefined) query.flexible = filters.flexible ? "true" : "false";
        if (filters.nights !== undefined) query.nights = filters.nights;

        // Guests - only add if not default values (adults=1, children=0, pets=0)
        if (filters.adults !== undefined && filters.adults !== 1) query.adults = filters.adults;
        if (filters.children !== undefined && filters.children !== 0) query.children = filters.children;
        if (filters.pets) query.pets = filters.pets;

        // Pagination (pageSize is handled by API, not in URL)
        if (filters.page !== undefined) query.page = filters.page;

        // Map bounds (camelCase)
        if (filters.neLat !== undefined) query.neLat = filters.neLat;
        if (filters.neLng !== undefined) query.neLng = filters.neLng;
        if (filters.swLat !== undefined) query.swLat = filters.swLat;
        if (filters.swLng !== undefined) query.swLng = filters.swLng;

        // Map center and zoom
        if (filters.centerLat !== undefined) query.centerLat = filters.centerLat;
        if (filters.centerLng !== undefined) query.centerLng = filters.centerLng;
        if (filters.zoom !== undefined) query.zoom = filters.zoom;

        // Bedrooms (camelCase)
        if (filters.minBeds !== undefined) query.minBeds = filters.minBeds;
        if (filters.maxBeds !== undefined) query.maxBeds = filters.maxBeds;

        // Bathrooms (camelCase)
        if (filters.minBaths !== undefined) query.minBaths = filters.minBaths;
        if (filters.maxBaths !== undefined) query.maxBaths = filters.maxBaths;

        // Nightly rate (camelCase)
        if (filters.minRate !== undefined) query.minRate = filters.minRate;
        if (filters.maxRate !== undefined) query.maxRate = filters.maxRate;

        // Housing types - comma-separated: housingType=house,condo
        if (filters.housingTypes?.length) {
            query.housingType = filters.housingTypes.join(",");
        }

        // Amenities - comma-separated: amenities=hottub,pool
        if (filters.amenities?.length) {
            query.amenities = filters.amenities.join(",");
        }

        return `/search${buildQueryString(query)}`;
    },

    /**
     * Parse URL params directly to SearchFilters (camelCase format)
     * Uses checkIn/checkOut format for dates
     */
    /**
     * Compare parsed URL filters against defaults.
     * Returns true if all default keys match (ignores extra params in filters).
     */
    /**
     * Check if filters match the default US overview.
     * Treats zoom <= 4 with map bounds present as default (zoomed out = no specific location).
     */
    isDefault: (filters: Partial<SearchFilters>): boolean => {
        const zoom = filters.zoom ?? DEFAULT_FILTERS.zoom;
        const hasBounds = filters.neLat !== undefined && filters.neLng !== undefined && filters.swLat !== undefined && filters.swLng !== undefined;
        const hasLocation = Boolean(filters.location?.trim());

        if (!hasLocation && !hasBounds) return true;

        if (hasBounds && zoom !== undefined && zoom <= 4) return true;

        return (Object.keys(DEFAULT_FILTERS) as Array<keyof SearchFilters>).every((key) => {
            return filters[key] === DEFAULT_FILTERS[key];
        });
    },

    parse: (params: URLSearchParams): SearchFilters => {
        // Parse map bounds (camelCase)
        const neLat = parseFloat(params.get("neLat"));
        const neLng = parseFloat(params.get("neLng"));
        const swLat = parseFloat(params.get("swLat"));
        const swLng = parseFloat(params.get("swLng"));
        const centerLat = parseFloat(params.get("centerLat"));
        const centerLng = parseFloat(params.get("centerLng"));
        const zoom = parseNumber(params.get("zoom"));

        // Parse dates from URL format (MM-DD-YYYY) to ISO format (YYYY-MM-DD)
        const startDate = DateHelper.parseDateToISO(parseString(params.get("checkIn")));
        const endDate = DateHelper.parseDateToISO(parseString(params.get("checkOut")));

        // Parse housing types - comma-separated: housingType=house,condo
        const housingTypeParam = params.get("housingType");
        const housingTypes = housingTypeParam ? housingTypeParam.split(",").filter(Boolean) : [];

        // Parse amenities - comma-separated: amenities=hottub,pool
        const amenitiesParam = params.get("amenities");
        const amenities = amenitiesParam ? amenitiesParam.split(",").filter(Boolean) : [];

        return {
            // Location
            location: parseString(params.get("location")) ?? null,

            // Convert checkIn/checkOut or sd/ed from URL to startDate/endDate for backend
            startDate,
            endDate,

            // Flexible dates
            flexible: params.get("flexible") === "true" ? true : params.get("flexible") === "false" ? false : undefined,
            nights: parseNumber(params.get("nights")),

            // Guests
            adults: parseNumber(params.get("adults")),
            children: parseNumber(params.get("children")),
            pets: parseNumber(params.get("pets")),

            // Pagination (pageSize is handled by API, not in URL)
            page: parseNumber(params.get("page")),

            // Map bounds (camelCase)
            neLat,
            neLng,
            swLat,
            swLng,
            centerLat,
            centerLng,
            zoom,

            // Bedrooms (camelCase)
            minBeds: parseNumber(params.get("minBeds")),
            maxBeds: parseNumber(params.get("maxBeds")),

            // Bathrooms (camelCase)
            minBaths: parseNumber(params.get("minBaths")),
            maxBaths: parseNumber(params.get("maxBaths")),

            // Nightly rate (camelCase)
            minRate: parseNumber(params.get("minRate")),
            maxRate: parseNumber(params.get("maxRate")),

            // Housing types and Amenities
            housingTypes: housingTypes.length > 0 ? housingTypes : undefined,
            amenities: amenities.length > 0 ? amenities : undefined,
        };
    },
};
